import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Heart, Loader2, MessageCircle, Calendar, ArrowLeft } from "lucide-react";
import api, { fmtError } from "@/lib/api";

/**
 * Matches grid — mutual swipe likes. One card per match:
 *   avatar | name, age | latest activity title | "Chat" button.
 *
 * Data source: GET /api/swipe/matches — pre-joined with each user's most recent
 * hosted activity so we only fire a single request.
 */
export default function Matches() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/swipe/matches");
        if (alive) setMatches(data.matches || []);
      } catch (e) {
        toast.error(fmtError(e.response?.data?.detail) || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const rel = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return t("matches.justNow");
    if (diff < 3600) return t("matches.minsAgo", { n: Math.floor(diff / 60) });
    if (diff < 86400) return t("matches.hrsAgo", { n: Math.floor(diff / 3600) });
    if (diff < 604800) return t("matches.daysAgo", { n: Math.floor(diff / 86400) });
    return new Date(iso).toLocaleDateString(i18n.language);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#F4F1E8] text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="matches-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-4">
          <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full hover:bg-[#F4F1E8] flex items-center justify-center" aria-label="Back" data-testid="matches-back">
            <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <Heart className="w-6 h-6 text-[#00CED1]" strokeWidth={2.5} fill="#00CED1" />
          <h1 className="font-display font-black text-[26px] tracking-tight flex-1">{t("matches.title")}</h1>
        </div>
      </div>

      <div className="px-4 pt-6">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#7C828D]" /></div>
        ) : matches.length === 0 ? (
          <EmptyMatches t={t} onSwipe={() => nav("/swipe")} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {matches.map((m) => (
              <MatchCard key={m.user.id} match={m} rel={rel} t={t} onOpen={() => nav(`/u/${m.user.id}`)} onChat={() => nav(`/chats?peer=${m.user.id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MatchCard({ match, rel, t, onOpen, onChat }) {
  const { user, matched_at, latest_activity } = match;
  const photo = (user.photos && user.photos[0]) || user.avatar_url;
  return (
    <div className="bg-white rounded-3xl border-2 border-[#121212] shadow-[4px_4px_0_#121212] overflow-hidden" data-testid={`match-card-${user.id}`}>
      <button onClick={onOpen} className="w-full text-left flex gap-3 p-3 hover:bg-[#F4F1E8] transition-colors">
        {photo ? (
          <img src={photo} alt={user.name} className="w-20 h-20 rounded-2xl object-cover border-2 border-[#121212] shrink-0" />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-[#F4F1E8] border-2 border-[#121212] flex items-center justify-center font-display font-black text-2xl shrink-0">
            {(user.name || "?")[0]?.toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="font-display font-black text-lg truncate">{user.name}{user.age ? `, ${user.age}` : ""}</h3>
            {user.verified && <span className="bg-[#00CED1] text-black text-[9px] font-black px-1.5 py-0.5 rounded-full shrink-0">✓</span>}
          </div>
          <p className="text-[11px] text-[#7C828D] font-semibold uppercase tracking-wide mb-1">{t("matches.matchedRel", { when: rel(matched_at) })}</p>
          {latest_activity ? (
            <div className="flex items-center gap-1 text-xs text-[#3D424B] line-clamp-2">
              <Calendar className="w-3.5 h-3.5 shrink-0 text-[#00CED1]" strokeWidth={2.5} />
              <span className="truncate font-semibold">{latest_activity.title}</span>
            </div>
          ) : (
            <p className="text-xs text-[#7C828D] italic">{t("matches.noRecentActivity")}</p>
          )}
        </div>
      </button>
      <div className="border-t-2 border-[#121212]">
        <button
          onClick={onChat}
          className="w-full py-2.5 flex items-center justify-center gap-2 bg-[#00CED1] text-black font-black hover:brightness-95 active:translate-y-[1px] transition-all"
          data-testid={`match-chat-${user.id}`}
        >
          <MessageCircle className="w-4 h-4" strokeWidth={3} />
          {t("matches.chatCta")}
        </button>
      </div>
    </div>
  );
}

function EmptyMatches({ t, onSwipe }) {
  return (
    <div className="text-center py-20 max-w-sm mx-auto" data-testid="matches-empty">
      <div className="w-20 h-20 rounded-full bg-[#F4F1E8] border-2 border-[#121212] mx-auto mb-4 flex items-center justify-center">
        <Heart className="w-9 h-9 text-[#7C828D]" strokeWidth={2.5} />
      </div>
      <h3 className="font-display font-black text-xl mb-1">{t("matches.emptyTitle")}</h3>
      <p className="text-sm text-[#7C828D] mb-4">{t("matches.emptyBody")}</p>
      <button onClick={onSwipe} className="bg-[#00CED1] text-black font-black py-2.5 px-5 rounded-full border-2 border-[#121212] shadow-[3px_3px_0_#121212] active:translate-y-[1px]" data-testid="matches-go-swipe">
        {t("matches.startSwiping")}
      </button>
    </div>
  );
}
